import styled, { css } from 'styled-components';
import React, { PropsWithChildren } from 'react';
import { withCssSelector } from '../shared/withCssSelector';
import { ToggleSize, toggleSizes } from './ToggleSize';
import { transition } from '../themes/mainTheme';

// Knob width lives in a custom property so the track can widen it on press while
// the thrown offset — which depends on it — follows in the same transition.
const knobWidth = '--plecto-toggle-knob-width';

const StyledKnob = styled.span`
	position: absolute;
	top: 50%;
	border-radius: 100px;
	transition: ${transition('background', 'left', 'width')};
`;

const StyledTrack = styled.span`
	position: relative;
	box-sizing: border-box;
	flex: none;
	border-style: solid;
	border-radius: 100px;
	transition: ${transition('background', 'border-color')};
`;

const StyledToggle = styled.button<{ $size: ToggleSize }>`
	${props => css`${knobWidth}: ${props.theme.form.toggle.knob.width}px;`}
	display: inline-flex;
	align-items: center;
	gap: 8px;
	// The whole row is the click target: track plus label, sized vertically by
	// the toggle size and hugging its content so no dead space is clickable.
	// Same as form/input-label, which does this for checkbox and radio rows.
	min-height: ${props => props.theme.form.toggle.target[props.$size]}px;
	width: fit-content;
	padding: 0;
	border: none;
	background: none;
	color: ${props => props.theme.text.primary};
	font: inherit;
	text-align: left;
	cursor: pointer;
	user-select: none;

	${StyledTrack} {
		width: ${props => props.theme.form.toggle.track.width}px;
		height: ${props => props.theme.form.toggle.track.height}px;
		border-width: ${props => props.theme.form.toggle.track.stroke}px;
		background: ${props => props.theme.form.toggle.default.background};
		border-color: ${props => props.theme.form.toggle.default.border};
	}

	${StyledKnob} {
		width: var(${knobWidth});
		height: ${props => props.theme.form.toggle.knob.height}px;
		left: ${props => props.theme.form.toggle.track.padding}px;
		transform: translateY(-50%);
		background: ${props => props.theme.form.toggle.default.knob};
	}

	&:hover,
	&:active {
		${StyledTrack} {
			background: ${props => props.theme.form.toggle.engaged.background};
			border-color: ${props => props.theme.form.toggle.engaged.border};
		}

		${StyledKnob} {
			background: ${props => props.theme.form.toggle.engaged.knob};
		}
	}

	// Keyboard focus only — a mouse press already shows the stretched knob.
	&:focus-visible {
		outline: none;

		${StyledTrack} {
			border-color: ${props => props.theme.form.toggle.focus.border};
		}
	}

	&:active {
		${props => css`${knobWidth}: ${props.theme.form.toggle.knob.pressedWidth}px;`}
	}

	&[aria-checked='true'] {
		${StyledTrack} {
			background: ${props => props.theme.form.toggle.checked.background};
			border-color: ${props => props.theme.form.toggle.checked.border};
		}

		${StyledKnob} {
			// Percentages resolve against the padding box, so the knob lands the
			// same distance from the far edge as it rests from the near one.
			left: calc(100% - ${props => props.theme.form.toggle.track.padding}px - var(${knobWidth}));
			background: ${props => props.theme.form.toggle.checked.knob};
		}

		&:hover ${StyledKnob},
		&:active ${StyledKnob} {
			background: ${props => props.theme.form.toggle.checked.engagedKnob};
		}
	}

	&:disabled {
		cursor: default;
		opacity: ${props => props.theme.form.disabled.opacity};
	}
`;

export type ToggleProps = {
	className?: string;
	active: boolean;
	onToggle: (next: boolean) => unknown;
	size?: ToggleSize;
	disabled?: boolean;
}

export const Toggle = withCssSelector(React.forwardRef<HTMLButtonElement, PropsWithChildren<ToggleProps>>(
	function Toggle({
		className,
		active,
		onToggle,
		disabled = false,
		size = toggleSizes.large,
		children
	}, ref) {
		return <StyledToggle
			ref={ref}
			type={'button'}
			role={'switch'}
			aria-checked={active}
			disabled={disabled}
			className={className}
			$size={size}
			onClick={() => onToggle(!active)}
		>
			<StyledTrack><StyledKnob /></StyledTrack>
			{children}
		</StyledToggle>;
	}
), StyledToggle.toString());
